import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { useAuth } from '@/context/AuthContext';
import { useI18n } from '@/lib/i18n';
import { toast } from 'sonner';
import {
  BookOpen, Plus, Search, FileText, Globe, Database, RefreshCw, AlertTriangle,
  CheckCircle2, Clock, XCircle, ChevronRight, Trash2, MessageSquare, HelpCircle,
  ExternalLink, Activity
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const SOURCE_TYPES = [
  { value: 'faq', icon: HelpCircle, color: 'text-[#3B82F6]' },
  { value: 'article', icon: FileText, color: 'text-[#10B981]' },
  { value: 'url', icon: Globe, color: 'text-[#EF694C]' },
  { value: 'document', icon: Database, color: 'text-[#A855F7]' },
];

const STATUS_ICONS = {
  indexed: { icon: CheckCircle2, color: 'text-[#10B981]', bg: 'bg-[#10B981]/10' },
  processing: { icon: Clock, color: 'text-[#F59E0B]', bg: 'bg-[#F59E0B]/10' },
  error: { icon: XCircle, color: 'text-[#F43F5E]', bg: 'bg-[#F43F5E]/10' },
  pending: { icon: Clock, color: 'text-[#52525b]', bg: 'bg-white/5' },
};

export default function KnowledgePage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const tenantId = user?.tenant_id;
  const [sources, setSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: 'faq', name: '', content: '', url: '' });
  const [filter, setFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [indexHealth, setIndexHealth] = useState(null);
  const [reindexing, setReindexing] = useState(false);

  const fetchSources = () => {
    api.get(`/tenants/${tenantId}/knowledge`)
      .then(r => setSources(r.data))
      .catch(() => toast.error(t.error))
      .finally(() => setLoading(false));
  };

  const fetchHealth = () => {
    api.get(`/tenants/${tenantId}/knowledge/index-health`)
      .then(r => setIndexHealth(r.data))
      .catch(() => {});
  };

  useEffect(() => { fetchSources(); fetchHealth(); }, [tenantId]);

  // Polling for processing sources
  useEffect(() => {
    const hasProcessing = sources.some(s => s.status === 'processing');
    if (!hasProcessing) return;
    const timer = setInterval(() => { fetchSources(); fetchHealth(); }, 4000);
    return () => clearInterval(timer);
  }, [sources]);

  const handleAdd = async (e) => {
    e.preventDefault();
    const contentField = form.type === 'url' ? form.url : form.content;
    if (!form.name || !contentField) { toast.error(t.provideNameAndContent); return; }
    setAdding(true);
    try {
      const payload = { type: form.type, name: form.name };
      if (form.type === 'url') {
        payload.url = form.url;
        payload.content = '';
      } else {
        payload.content = form.content;
      }
      await api.post(`/tenants/${tenantId}/knowledge`, payload);
      toast.success(t.sourceAdded);
      setShowAdd(false);
      setForm({ type: 'faq', name: '', content: '', url: '' });
      fetchSources();
      setTimeout(fetchHealth, 5000);
    } catch { toast.error(t.error); }
    finally { setAdding(false); }
  };

  const handleDelete = async (src) => {
    if (!window.confirm(t.deleteSourceConfirm)) return;
    try {
      await api.delete(`/tenants/${tenantId}/knowledge/${src.id}`);
      setSources(prev => prev.filter(s => s.id !== src.id));
      toast.success(t.sourceDeleted);
      fetchHealth();
    } catch { toast.error(t.error); }
  };

  const handleReindex = async () => {
    if (!window.confirm(t.reindexConfirm)) return;
    setReindexing(true);
    try {
      await api.post(`/tenants/${tenantId}/knowledge/reindex-all`);
      toast.success(t.reindexQueued);
      fetchSources();
      setTimeout(fetchHealth, 8000);
    } catch { toast.error(t.error); }
    finally { setReindexing(false); }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const r = await api.post(`/tenants/${tenantId}/knowledge/search?query=${encodeURIComponent(searchQuery)}&top_k=5`);
      setSearchResults(r.data.chunks || []);
    } catch { toast.error(t.error); }
    finally { setSearching(false); }
  };

  const handleSync = async (src) => {
    try {
      await api.post(`/tenants/${tenantId}/knowledge/${src.id}/sync`);
      toast.success(t.reingestionStarted);
      fetchSources();
    } catch { toast.error(t.syncFailed); }
  };

  const stats = {
    total: sources.length,
    indexed: sources.filter(s => s.status === 'indexed').length,
    processing: sources.filter(s => s.status === 'processing').length,
    chunks: sources.reduce((a, s) => a + (s.chunk_count || 0), 0),
    errors: sources.filter(s => s.status === 'error').length,
  };

  const filtered = sources.filter(s =>
    !filter || s.name?.toLowerCase().includes(filter.toLowerCase()) || s.type?.toLowerCase().includes(filter.toLowerCase())
  );

  const healthColor = indexHealth?.health === 'healthy' ? '#10B981' : indexHealth?.health === 'degraded' ? '#F59E0B' : '#F43F5E';

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h2 className="font-heading font-bold text-2xl text-white" data-testid="kb-title">{t.kbTitle}</h2>
          <p className="text-[#a1a1aa] text-sm mt-1">
            {t.kbSourcesIndexed(stats.indexed, stats.total)}
            {stats.processing > 0 && <span className="text-[#F59E0B]"> {t.kbProcessing(stats.processing)}</span>}
          </p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="add-source-btn"
          className="flex items-center gap-2 h-10 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white text-sm font-semibold rounded-lg shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all active:scale-95">
          <Plus className="w-4 h-4" /> {t.addSource}
        </button>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 animate-fade-in-up stagger-1">
        {[
          { label: t.totalSources, value: stats.total, icon: BookOpen, color: '#a1a1aa' },
          { label: t.indexed, value: stats.indexed, icon: CheckCircle2, color: '#10B981' },
          { label: t.processing, value: stats.processing, icon: Clock, color: '#F59E0B' },
          { label: t.totalChunks, value: stats.chunks, icon: Database, color: '#3B82F6' },
          { label: t.errors, value: stats.errors, icon: XCircle, color: '#F43F5E' },
        ].map(s => (
          <div key={s.label} className="bg-[#121214] border border-white/10 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-2">
              <s.icon className="w-4 h-4" style={{ color: s.color }} />
              <span className="text-xs text-[#71717a] font-medium">{s.label}</span>
            </div>
            <p className="text-xl font-bold text-white">{s.value}</p>
          </div>
        ))}
      </div>

      {/* Index Health */}
      {indexHealth && (
        <div className={`bg-[#121214] border rounded-xl p-5 animate-fade-in-up stagger-2`} style={{ borderColor: `${healthColor}30` }}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4" style={{ color: healthColor }} />
              <h3 className="font-heading font-semibold text-sm text-white">{t.indexHealth}</h3>
              <span className="text-xs px-2 py-0.5 rounded-full font-medium" style={{ color: healthColor, backgroundColor: `${healthColor}15` }}>
                {indexHealth.health === 'healthy' ? t.healthy : indexHealth.health === 'degraded' ? t.degraded : t.indexError}
              </span>
            </div>
            {(indexHealth.reindex_required > 0 || !indexHealth.is_consistent) && (
              <button onClick={handleReindex} disabled={reindexing} data-testid="reindex-all-btn"
                className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium bg-[#F59E0B]/10 text-[#F59E0B] border border-[#F59E0B]/20 rounded-lg hover:bg-[#F59E0B]/20 transition-all disabled:opacity-50">
                <RefreshCw className={`w-3.5 h-3.5 ${reindexing ? 'animate-spin' : ''}`} /> {t.reindexAll}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: t.provider, value: indexHealth.current_embedding?.provider || '-' },
              { label: t.model, value: indexHealth.current_embedding?.model || '-' },
              { label: t.dimensions, value: indexHealth.current_embedding?.dimensions || '-' },
              { label: t.backend, value: indexHealth.current_embedding?.backend || '-' },
            ].map(item => (
              <div key={item.label} className="bg-[#09090b] rounded-lg p-3 border border-white/5">
                <p className="text-[10px] text-[#52525b] uppercase tracking-wider mb-1">{item.label}</p>
                <p className="text-sm text-white font-mono">{item.value}</p>
              </div>
            ))}
          </div>
          {indexHealth.reindex_required > 0 && (
            <div className="mt-3 p-3 bg-[#F59E0B]/5 border border-[#F59E0B]/10 rounded-lg">
              <p className="text-sm text-[#d4d4d8]">
                <AlertTriangle className="w-4 h-4 text-[#F59E0B] inline mr-1.5" />
                {t.reindexWarning(indexHealth.reindex_required)}
              </p>
            </div>
          )}
          {!indexHealth.is_consistent && indexHealth.mixed_models?.length > 1 && (
            <div className="mt-3 p-3 bg-[#F43F5E]/5 border border-[#F43F5E]/10 rounded-lg">
              <p className="text-sm text-[#d4d4d8]">
                <AlertTriangle className="w-4 h-4 text-[#F43F5E] inline mr-1.5" />
                {t.mixedModelsWarning(indexHealth.mixed_models.join(', '))}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Semantic Search */}
      <div className="bg-[#121214] border border-white/10 rounded-xl p-5 animate-fade-in-up stagger-3">
        <div className="flex items-center gap-2 mb-3">
          <Search className="w-4 h-4 text-[#EF694C]" />
          <h3 className="font-heading font-semibold text-sm text-white">{t.semanticSearch}</h3>
        </div>
        <p className="text-xs text-[#71717a] mb-3">{t.semanticSearchHint}</p>
        <form onSubmit={handleSearch} className="flex gap-2">
          <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder={t.searchPlaceholder} data-testid="semantic-search-input"
            className="flex-1 h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
          <button type="submit" disabled={searching} data-testid="semantic-search-btn"
            className="h-10 px-4 bg-[#EF694C]/10 text-[#EF694C] border border-[#EF694C]/20 rounded-lg text-sm font-medium hover:bg-[#EF694C]/20 transition-all disabled:opacity-50">
            {searching ? <RefreshCw className="w-4 h-4 animate-spin" /> : t.search}
          </button>
        </form>
        {searchResults && (
          <div className="mt-4 space-y-2">
            {searchResults.length === 0 ? (
              <p className="text-sm text-[#52525b] py-4 text-center">{t.noSearchResults}</p>
            ) : searchResults.map((c, i) => (
              <div key={i} className="bg-[#09090b] border border-white/5 rounded-lg p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] text-[#52525b] font-mono">Score: {c.score?.toFixed(4)}</span>
                  <span className="text-[10px] text-[#52525b]">{c.source_name}</span>
                </div>
                <p className="text-sm text-[#d4d4d8]">{c.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Sources List */}
      <div className="animate-fade-in-up stagger-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold text-base text-white">{t.totalSources}</h3>
          <input value={filter} onChange={e => setFilter(e.target.value)}
            placeholder={t.filterSources} data-testid="source-filter-input"
            className="h-8 w-48 bg-[#121214] border border-white/10 rounded-lg px-3 text-white text-xs focus:outline-none focus:border-[#EF694C] transition-all" />
        </div>
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-7 h-7 border-2 border-[#EF694C]/30 border-t-[#EF694C] rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 bg-[#121214] border border-white/10 rounded-xl px-6" data-testid="kb-empty-state">
            <BookOpen className="w-10 h-10 text-[#52525b] mx-auto mb-3" />
            <p className="text-[#a1a1aa] text-base mb-2">{t.noKBSources}</p>
            <p className="text-[#71717a] text-sm max-w-xl mx-auto mb-4 leading-relaxed">{t.kbEmptyExplain}</p>
            <button onClick={() => setShowAdd(true)} data-testid="kb-empty-add-btn"
              className="inline-flex items-center gap-2 h-10 px-4 bg-[#EF694C] hover:bg-[#d65d43] text-white text-sm font-semibold rounded-lg transition-all active:scale-95">
              <Plus className="w-4 h-4" /> {t.addFirstSource}
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map(src => {
              const st = STATUS_ICONS[src.status] || STATUS_ICONS.pending;
              const typeConf = SOURCE_TYPES.find(s => s.value === src.type) || SOURCE_TYPES[0];
              return (
                <div key={src.id} data-testid={`source-card-${src.id}`}
                  className="bg-[#121214] border border-white/10 rounded-xl p-4 hover:border-white/20 transition-all">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`w-9 h-9 rounded-lg ${st.bg} flex items-center justify-center flex-shrink-0`}>
                        <typeConf.icon className={`w-4 h-4 ${typeConf.color}`} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-white truncate">{src.name}</p>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                          <span className={`text-xs ${st.color} flex items-center gap-1`}>
                            <st.icon className="w-3 h-3" />
                            {src.status === 'indexed' ? t.indexed :
                             src.status === 'processing' ? t.processing :
                             src.status === 'error' ? t.indexError : src.status}
                          </span>
                          {src.chunk_count > 0 && (
                            <span className="text-[10px] text-[#52525b]">{src.chunk_count} {t.chunks}</span>
                          )}
                          {src.embedding_provider && (
                            <span className="text-[10px] text-[#52525b] font-mono">{src.embedding_provider}/{src.embedding_model}</span>
                          )}
                        </div>
                        {/* Error guidance */}
                        {src.status === 'error' && src.error_msg && (
                          <div className="mt-2 p-2 bg-[#F43F5E]/5 border border-[#F43F5E]/10 rounded-lg">
                            <p className="text-xs text-[#F43F5E] mb-1">{t.error}: {src.error_msg?.slice(0, 120)}</p>
                            <p className="text-xs text-[#71717a]">
                              {src.error_msg?.includes('dimension') ?
                                (t.lang === 'de' ? 'Dimensionskonflikt erkannt. Bitte alle Quellen neu indexieren.' : 'Dimension conflict detected. Please reindex all sources.') :
                                (t.lang === 'de' ? 'Versuchen Sie, die Quelle erneut zu synchronisieren.' : 'Try syncing the source again.')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0 ml-3">
                      {src.status === 'error' && (
                        <button onClick={() => handleSync(src)} data-testid={`sync-source-${src.id}`}
                          className="p-2 text-[#a1a1aa] hover:text-[#F59E0B] hover:bg-[#F59E0B]/10 rounded-lg transition-colors" title="Re-sync">
                          <RefreshCw className="w-4 h-4" />
                        </button>
                      )}
                      <button onClick={() => handleDelete(src)} data-testid={`delete-source-${src.id}`}
                        className="p-2 text-[#a1a1aa] hover:text-[#F43F5E] hover:bg-[#F43F5E]/10 rounded-lg transition-colors" title={t.delete}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Add Source Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg" data-testid="add-source-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold text-xl">{t.addKBTitle}</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-[#71717a] mb-4">{t.addKBDesc}</p>
          <form onSubmit={handleAdd} className="space-y-4">
            {/* Source type selector */}
            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-2">{t.sourceType}</label>
              <div className="grid grid-cols-4 gap-2">
                {SOURCE_TYPES.map(st => (
                  <button key={st.value} type="button" onClick={() => setForm(p => ({ ...p, type: st.value, content: '', url: '' }))}
                    data-testid={`source-type-${st.value}`}
                    className={`flex flex-col items-center gap-1.5 p-3 rounded-lg border text-xs font-medium transition-all ${
                      form.type === st.value ? 'border-[#EF694C] bg-[#EF694C]/10 text-white' : 'border-white/10 text-[#a1a1aa] hover:border-white/20'
                    }`}>
                    <st.icon className={`w-5 h-5 ${form.type === st.value ? 'text-[#EF694C]' : st.color}`} />
                    {t[st.value]}
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-[#52525b] mt-1.5">
                {form.type === 'faq' && t.faqHint}
                {form.type === 'article' && t.articleHint}
                {form.type === 'url' && t.urlHint}
                {form.type === 'document' && t.documentHint}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.sourceName}</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                placeholder={form.type === 'faq' ? 'z.B. Login-Hilfe FAQ' : form.type === 'url' ? 'z.B. Help Center' : 'Name'}
                data-testid="source-name-input"
                className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all" />
            </div>

            {form.type === 'url' ? (
              <div>
                <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.sourceURL}</label>
                <input value={form.url} onChange={e => setForm(p => ({ ...p, url: e.target.value }))}
                  placeholder="https://help.example.com/article" data-testid="source-url-input"
                  className="w-full h-10 bg-[#09090b] border border-white/10 rounded-lg px-3 text-white text-sm font-mono focus:outline-none focus:border-[#EF694C] transition-all" />
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-[#d4d4d8] mb-1.5">{t.sourceContent}</label>
                <textarea value={form.content} onChange={e => setForm(p => ({ ...p, content: e.target.value }))}
                  placeholder={t.contentPlaceholder} rows={form.type === 'faq' ? 6 : 10} data-testid="source-content-input"
                  className="w-full bg-[#09090b] border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#EF694C] transition-all resize-y" />
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="button" onClick={() => setShowAdd(false)}
                className="flex-1 h-10 border border-white/10 text-[#a1a1aa] hover:text-white hover:bg-white/5 rounded-lg text-sm font-medium transition-all">
                {t.cancel}
              </button>
              <button type="submit" disabled={adding} data-testid="submit-source-btn"
                className="flex-1 h-10 bg-[#EF694C] hover:bg-[#d65d43] text-white font-semibold rounded-lg text-sm shadow-[0_0_20px_rgba(239,105,76,0.3)] transition-all disabled:opacity-60">
                {adding ? t.adding : t.addAndIngest}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
